/**
 * Recency of a catalog entry (issue #620, cold-start treatments).
 *
 * Lives here rather than inline in the three components that need it — card, list
 * row and detail page — for the reason `lib/feedback` and `lib/adoption` already
 * exist: a rule evaluated in three places from one definition can only disagree
 * through a rendering bug, whereas three inline date comparisons drift the first
 * time someone "clarifies" one of them.
 *
 * WHAT `firstDiscoveredAt` ACTUALLY MEANS, because the badge inherits it: the moment
 * the MARKETPLACE first catalogued the repo, not the moment the repo was created. A
 * five-year-old repo that adds a `.claude-plugin/plugin.json` today is "new" here.
 * That is the right reading for #620's purpose — the badge exists to say "a low vote
 * count and a missing usage figure are expected of this entry", and both are true of
 * a long-lived repo on its first day in the catalog.
 */

/** Window #620 defines as "recently discovered". */
export const NEW_WINDOW_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Was this entry first catalogued within the last `NEW_WINDOW_DAYS`?
 *
 * `now` is injectable so the boundary can be tested at a fixed instant rather than
 * through fake timers — the same reason the adoption attribution takes a clock.
 *
 * Unparseable or absent input returns false: an entry whose provenance we cannot
 * read is not claimed to be new. `firstDiscoveredAt` is required by Contract 3, but
 * this is a rendering path fed by a fetched artifact, so it does not get to assume
 * the field survived.
 *
 * A timestamp in the FUTURE counts as new. It should not happen, but a few seconds
 * of clock skew between the processor and the browser would otherwise flip a
 * genuinely new entry to "not new" — the opposite of the intent — and the alternative
 * (rejecting negatives) fails in exactly the case the badge matters most.
 *
 * Unlike `formatDataThroughDate`, `new Date(...)` is correct here: Contract 3's
 * `firstDiscoveredAt` is a full ISO-8601 timestamp with an offset, not the bare
 * `YYYY-MM-DD` that parses as UTC midnight and renders as the previous day west of
 * UTC. This is a comparison of instants, not a date to display.
 */
export function isRecentlyDiscovered(
  firstDiscoveredAt: string | undefined,
  now: number = Date.now(),
): boolean {
  if (!firstDiscoveredAt) return false;
  const discoveredAt = new Date(firstDiscoveredAt).getTime();
  if (Number.isNaN(discoveredAt)) return false;
  return now - discoveredAt <= NEW_WINDOW_DAYS * MS_PER_DAY;
}